<template>
  <VDataGridV2
      :loader="dataLoader"
      group-children-property="positions"
      :group-context-menu="ShipmentsService.contextMenu"
      :skeleton-row-count="16"
      paginated
			@group:dblclick="openShipmentCard"
  >
    <VDataGridGroupV2>
      <template #default="{ parent: row }">

				<CommentsButton entity-type-name="shipment" :entity="row"
                        v-if="$core.currentPartner.isBuyer === true"/>

				<div>
					<VLink @click.stop="openShipmentCard(row)">
						№{{ $core.utils.string.formatWithDelimiter(row.id) }}
					</VLink>
				</div>

				<span>
					{{ row.documentType.name }}
					№{{ row.documentNumber }}
					от {{ $core.utils.date.format(row.documentDate) }}
				</span>

				<span>
					{{ row.sellerCompany.name }}
				</span>

				<VBadges>
					<VBadge v-if="!row.isOfferBased" class="primary">Прямой</VBadge>
					<VBadge v-if="row.isSamples">Образцы</VBadge>
					<VBadge v-if="row.externalNumber">Техносклад №: {{ row.externalNumber || '--' }}</VBadge>
					<VBadge v-if="row.isReceived">Получена</VBadge>
				</VBadges>

      </template>
    </VDataGridGroupV2>

    <VDataGridColumnV2
        id="position-action"
        class="position-action"
        content-align="right"
        width="4%"
    >
      <template #default="{ row }">
				<CommentsButton entity-type-name="orderPosition" :entity="row.orderPosition"
                        v-if="$core.currentPartner.isBuyer === true"/>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        id="id"
        sort-default-value="desc"
        label="№"
        sortable
        content-align="left"
        width="100px"
    >
      <template #default="{ row }">
        {{ row.orderPosition.formattedNumber }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        id="buyersCode"
        label="Арт"
        width="80px"
        content-align="center"
    >
      <template #default="{ row }">
        <VLink @click.stop="OrdersService.showCodeInfoPopup(row.orderPosition, $event.currentTarget)">
          {{ row.orderPosition.code ?? '--' }}
        </VLink>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2 id="name" width="600px">
      <template #label>
        <div>Товар</div>
        <div class="description">
          производитель / материал
        </div>
      </template>
      <template #default="{ row }">
        {{ row.orderPosition.name }}
        <div class="description">
          <template v-if="row.orderPosition.isRawMaterialsAndPackage && row.orderPosition.manufacturer">
            Производитель: {{ row.orderPosition.manufacturer.name }}
          </template>

          <template v-if="row.orderPosition.isPrinting && row.orderPosition.material">
            Материал: {{ row.orderPosition.material }}
          </template>
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        id="qty"
        content-align="right"
        width="130px"
    >
      <template #label>
        Кол-во
        <div class="description">
          Кол-во в заказе
        </div>
      </template>
      <template #default="{ row }">
        <div class="nowrap">
          {{ row.qty.formatSmart(row.orderPosition.unit) }}
        </div>
        <div class="description">
          {{ row.orderPosition.qty.formatSmart(row.orderPosition.unit) }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        id="sum"
        content-align="right"
        width="150px"
    >
      <template #label>
        Цена товара
        <div class="description">
          Сумма
        </div>
      </template>
      <template #default="{ row }">
        <div>
          {{ row.priceFull.format(row.orderCurrency, row.unit) }}
        </div>

        <div class="description">
          {{ row.sumFull.format(row.orderCurrency) }}
        </div>
      </template>
    </VDataGridColumnV2>
  </VDataGridV2>
</template>

<script lang="ts">
import type BasePageFilter       from '@/domains/BasePageFilter/BasePageFilter';
import ShipmentsService          from '@/domains/Shipments/ShipmentsService';
import Shipment                  from '@/domains/Shipments/Shipment';
import OrdersService             from '@/domains/Orders/OrdersService';
import DataGridLoader            from '@/lib/components/VDataGridV2/DataGridLoader';

export default defineComponent({
  name: 'OrdersInDeliveryBuyer',

  props: {
    /**
     * Класс страничного фильтра (localStorage). Передаётся, когда реестр
     * монтируется внутри страницы с фильтром (воронка). Без него фильтр не подключается.
     */
    filterClass: {
      type:    Function as unknown as () => typeof BasePageFilter,
      default: null,
    },
    /** Показывать принятые отгрузки. Воронка передаёт false — там нужны ещё не принятые */
    isReceived: {
      type:    Boolean,
      default: true,
    },

    params: {
      type: Object as PropType<Parameters<typeof ShipmentsService.getListPaginated>[0]>,
      default: null,
    },
  },


  setup() {
    return {
      ShipmentsService,
      OrdersService,
    }
  },

  data() {
    return {
      dataLoader: new DataGridLoader(ShipmentsService.getListPaginated)
          .useExtraParams({ isReceived: this.isReceived, requireCommentsInfo: true })
          .useReactiveSource(() => {
            return {
              ...this.params
            }
          }),
    };
  },

  methods: {
    async openShipmentCard(row: Shipment): Promise<void> {
      if (await this.$core.dialogs.ShipmentCard.showAsPromise({id: row.id})) {
        this.dataLoader.refresh();
      }
    },

    refresh(): void {
      this.dataLoader.refresh();
    },
  },
});
</script>
